import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import {
  addGHMoreServices,
  GH_SERVICE_DURATIONS,
} from "../../../services/guestHouseServicesApi";

const COMMON_SERVICES = [
  { name: "Laundry Service", price: 500, duration: "One-Time" },
  { name: "Special Nursing Care", price: 1200, duration: "Day" },
  { name: "Airport Drop / Pickup", price: 1500, duration: "One-Time" },
  { name: "Hospital Shuttle", price: 600, duration: "One-Time" },
  { name: "Special Meal Plan (3x Daily)", price: 800, duration: "Day" },
  { name: "Wheelchair Rental", price: 300, duration: "Day" },
  { name: "Extra Bed & Linen", price: 500, duration: "Day" },
  { name: "Oxygen Concentrator Rental", price: 1000, duration: "Day" },
  { name: "Attendant Bed", price: 400, duration: "Day" },
  { name: "High-Speed Wi-Fi Router", price: 200, duration: "Day" },
];

export default function AddServicesModal({ open, onClose, record, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([
    {
      serviceName: "",
      price: "",
      quantity: 1,
      duration: "One-Time",
      discount_percentage: 0,
      description: "",
    },
  ]);

  if (!open || !record) return null;

  const handleServiceChange = (index, field, value) => {
    setServices((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSelectPreset = (index, preset) => {
    setServices((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        serviceName: preset.name,
        price: preset.price,
        duration: preset.duration,
      };
      return next;
    });
  };

  const addRow = () => {
    setServices((prev) => [
      ...prev,
      {
        serviceName: "",
        price: "",
        quantity: 1,
        duration: "One-Time",
        discount_percentage: 0,
        description: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (services.length === 1) return;
    setServices((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return services.reduce((sum, item) => {
      const p = Number(item.price) || 0;
      const q = Number(item.quantity) || 1;
      const d = Number(item.discount_percentage) || 0;
      const base = p * q;
      return sum + (d ? base - (base * d) / 100 : base);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validServices = services.filter((s) => s.serviceName.trim() && Number(s.price) >= 0);
    if (validServices.length === 0) {
      Swal.fire("Required", "Please provide at least one valid service with a name and price.", "warning");
      return;
    }

    setLoading(true);
    try {
      await addGHMoreServices(record._id || record.guestHouseServiceId, {
        services: validServices.map((s) => ({
          serviceName: s.serviceName.trim(),
          price: Number(s.price),
          quantity: Number(s.quantity) || 1,
          duration: s.duration,
          discount_percentage: Number(s.discount_percentage) || 0,
          description: s.description?.trim() || "",
        })),
      });

      Swal.fire({
        title: "Services Added!",
        text: `${validServices.length} service(s) added to ${record.guestHouseServiceId}.`,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error adding services:", err);
      Swal.fire("Error", typeof err === "string" ? err : "Failed to add services", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="gh-modal-dialog gh-modal-md"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          height: "min(800px, calc(100vh - 48px))",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        },
      }}
    >
      <div
        className="gh-modal-header"
        style={{
          background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="fa fa-plus-circle fs-5" />
          <div>
            <h5 className="mb-0 fw-bold" style={{ fontSize: "16px" }}>
              Add Additional Services
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)" }}>
              {record.guestHouseServiceId} • {record.patient_name} ({record.guestHouseName})
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <DialogContent style={{ padding: "20px", flex: "1 1 auto", overflowY: "auto" }}>
          {/* Quick Presets */}
          <div className="mb-3">
            <span className="small fw-semibold text-muted d-block mb-1">
              Quick Suggestions:
            </span>
            <div className="d-flex flex-wrap gap-1">
              {COMMON_SERVICES.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                  style={{ fontSize: "11px" }}
                  onClick={() => {
                    const emptyIdx = services.findIndex((s) => !s.serviceName.trim());
                    if (emptyIdx !== -1) {
                      handleSelectPreset(emptyIdx, preset);
                    } else {
                      setServices((prev) => [
                        ...prev,
                        {
                          serviceName: preset.name,
                          price: preset.price,
                          quantity: 1,
                          duration: preset.duration,
                          discount_percentage: 0,
                          description: "",
                        },
                      ]);
                    }
                  }}
                >
                  + {preset.name} (₹{preset.price})
                </button>
              ))}
            </div>
          </div>

          {/* Service items table/cards */}
          <div className="d-flex flex-column gap-2 mb-3">
            {services.map((item, index) => {
              const lineTotal =
                (Number(item.price) || 0) * (Number(item.quantity) || 1) -
                (((Number(item.price) || 0) *
                  (Number(item.quantity) || 1) *
                  (Number(item.discount_percentage) || 0)) /
                  100);

              return (
                <div
                  key={index}
                  className="p-3 border rounded bg-white shadow-sm position-relative"
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="badge bg-light text-primary border">
                      Service Item #{index + 1}
                    </span>
                    {services.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm py-0 px-2"
                        style={{ fontSize: "11px" }}
                        onClick={() => removeRow(index)}
                        title="Remove row"
                      >
                        <i className="fa fa-trash me-1" /> Remove
                      </button>
                    )}
                  </div>

                  <div className="row g-2 align-items-center">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-0">Service Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Airport Drop, Laundry"
                        value={item.serviceName}
                        onChange={(e) => handleServiceChange(index, "serviceName", e.target.value)}
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted mb-0">Duration</label>
                      <select
                        value={item.duration}
                        onChange={(e) => handleServiceChange(index, "duration", e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {GH_SERVICE_DURATIONS.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted mb-0">Price (₹) *</label>
                      <input
                        type="number"
                        min="0"
                        required
                        placeholder="Price"
                        value={item.price}
                        onChange={(e) => handleServiceChange(index, "price", e.target.value)}
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-md-1">
                      <label className="form-label small text-muted mb-0">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleServiceChange(index, "quantity", e.target.value)}
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-md-1">
                      <label className="form-label small text-muted mb-0">Disc %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount_percentage}
                        onChange={(e) =>
                          handleServiceChange(index, "discount_percentage", e.target.value)
                        }
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-md-2 text-end">
                      <label className="form-label small text-muted mb-0 d-block">Subtotal</label>
                      <span className="fw-bold text-success fs-6">
                        ₹{lineTotal.toLocaleString()}
                      </span>
                    </div>
                    <div className="col-12">
                      <input
                        type="text"
                        placeholder="Optional description / details (e.g. 2 hours daily, vegetarian)"
                        value={item.description}
                        onChange={(e) => handleServiceChange(index, "description", e.target.value)}
                        className="form-control form-control-sm text-muted"
                        style={{ fontSize: "11.5px" }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              onClick={addRow}
            >
              <i className="fa fa-plus" /> Add Another Item
            </button>
          </div>
        </DialogContent>

        {/* Pinned Sticky Footer Actions */}
        <div
          className="gh-modal-footer"
          style={{
            padding: "12px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <div>
            <span className="small text-muted me-2" style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase" }}>
              Total Addition:
            </span>
            <strong className="fs-5 text-dark">₹{calculateSubtotal().toLocaleString()}</strong>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-light btn-sm px-3 border"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
              disabled={loading}
              style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Adding...
                </>
              ) : (
                <>
                  <i className="fa fa-check" /> Confirm & Add Services
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
